export interface RealEstateListing {
  id: string;
  slug: string;
  type: 'listing' | 'rental' | 'guide';
  unitLabel: string;
  price: number;
  priceDisplay: string;
  beds?: number;
  baths?: number;
  sqft?: number;
  summary: string;
  description: string;
  propertyType: 'Tower 1' | 'Tower 2' | 'Townhome' | 'Commercial';
  status: 'Active' | 'Pending' | 'Sold' | 'Leased' | 'Available' | 'Off Market';
  tags: string[];
  features?: string[];
  monthlyRent?: string;
  isRental?: boolean;
  guideTitle?: string;
  guideBody?: RealEstateGuideSection[];
}

export interface RealEstateGuideSection {
  heading?: string;
  content: string;
  list?: string[];
}

export interface SpecialistSpotlight {
  name: string;
  title: string;
  bio: string;
  specialties: string[];
  note: string;
}

export const mockListingsRE: RealEstateListing[] = [
  {
    id: 're-1',
    slug: 'tower-2-unit-2801',
    type: 'listing',
    unitLabel: 'Tower 2 — Unit 2801',
    price: 875000,
    priceDisplay: '$875,000',
    beds: 2,
    baths: 2,
    sqft: 1480,
    summary: '2 bed / 2 bath · 1,480 sqft · Tower 2 · High Floor',
    description: 'Exceptional high-floor unit in Tower 2 with panoramic Strip views. Open floor plan, floor-to-ceiling windows, updated kitchen with quartz countertops and premium appliances. Primary suite with spa-style bath. Building amenities include pool, fitness center, and 24-hour concierge. One assigned parking space included.',
    propertyType: 'Tower 2',
    status: 'Active',
    tags: ['High Floor', 'Strip Views', 'Updated Kitchen', 'Concierge'],
    features: [
      'Floor-to-ceiling windows with Strip views',
      'Updated kitchen with quartz countertops',
      'Premium appliances',
      'Spa-style primary bath',
      'In-unit washer/dryer',
      'One assigned parking space',
      '24-hour concierge',
      'Pool and fitness center access',
    ],
  },
  {
    id: 're-2',
    slug: 'tower-1-unit-1504',
    type: 'listing',
    unitLabel: 'Tower 1 — Unit 1504',
    price: 620000,
    priceDisplay: '$620,000',
    beds: 1,
    baths: 1,
    sqft: 920,
    summary: '1 bed / 1 bath · 920 sqft · Tower 1 · Mid Floor',
    description: 'Well-maintained mid-floor unit in Tower 1. Clean, move-in ready with updated bath and kitchen. City views from the balcony. Ideal for a primary residence or investment property. Strong rental history.',
    propertyType: 'Tower 1',
    status: 'Active',
    tags: ['Move-In Ready', 'City Views', 'Investment', 'Balcony'],
    features: [
      'Updated kitchen and bath',
      'Private balcony with city views',
      'In-unit washer/dryer',
      'One assigned parking space',
      'Strong rental history',
    ],
  },
  {
    id: 're-3',
    slug: 'panorama-townhome-th12',
    type: 'listing',
    unitLabel: 'Panorama Townhome — TH-12',
    price: 1150000,
    priceDisplay: '$1,150,000',
    beds: 3,
    baths: 2.5,
    sqft: 2100,
    summary: '3 bed / 2.5 bath · 2,100 sqft · Townhome · Private Entry',
    description: 'A rare Panorama townhome offering private street-level entry, a two-car garage, and the full amenity access of the towers. Three bedrooms across two levels, private patio, and a layout that feels more like a house than a condo. Ideal for families or residents who want more space without leaving the Panorama community.',
    propertyType: 'Townhome',
    status: 'Active',
    tags: ['Townhome', 'Private Entry', 'Two-Car Garage', 'Patio', 'Rare'],
    features: [
      'Private street-level entry',
      'Two-car attached garage',
      'Private patio',
      'Three bedrooms across two levels',
      'Full tower amenity access',
      'Rare townhome format',
    ],
  },
  {
    id: 're-4',
    slug: 'tower-2-unit-3305-rental',
    type: 'rental',
    unitLabel: 'Tower 2 — Unit 3305',
    price: 3200,
    priceDisplay: '$3,200/mo',
    beds: 2,
    baths: 2,
    sqft: 1380,
    summary: '2 bed / 2 bath · 1,380 sqft · Tower 2 · High Floor',
    description: 'Fully furnished high-floor rental in Tower 2. Available for 6-month or 12-month lease. Panoramic Strip views, premium furnishings, and all building amenities included. Ideal for a second-home arrangement or executive rental.',
    propertyType: 'Tower 2',
    status: 'Available',
    tags: ['Furnished', 'Strip Views', 'Flexible Lease', 'Executive'],
    isRental: true,
    monthlyRent: '$3,200/mo',
    features: [
      'Fully furnished',
      'Panoramic Strip views',
      'Flexible lease terms (6 or 12 months)',
      'All building amenities included',
      'One parking space',
    ],
  },
  {
    id: 're-5',
    slug: 'tower-1-unit-2204-pending',
    type: 'listing',
    unitLabel: 'Tower 1 — Unit 2204',
    price: 740000,
    priceDisplay: '$740,000',
    beds: 2,
    baths: 2,
    sqft: 1320,
    summary: '2 bed / 2 bath · 1,320 sqft · Tower 1 · High Floor',
    description: 'Sale pending. High-floor corner unit in Tower 1 with dual exposures and exceptional light. Updated throughout.',
    propertyType: 'Tower 1',
    status: 'Pending',
    tags: ['Corner Unit', 'Dual Exposure', 'Updated', 'High Floor'],
    features: [
      'Corner unit with dual exposures',
      'Exceptional natural light',
      'Updated throughout',
      'High floor',
    ],
  },
];

export const marketSnapshotData = {
  lastUpdated: 'March 2026 (Mocked — for illustration only)',
  disclaimer: 'This market data is mocked for demonstration purposes only. It does not represent actual MLS data or current market conditions. For accurate market information, consult a licensed real estate professional.',
  stats: [
    { label: 'Avg. List Price (Tower Units)', value: '$745K', note: 'Mocked example' },
    { label: 'Avg. Price Per Sqft', value: '$520', note: 'Mocked example' },
    { label: 'Active Listings', value: '4', note: 'Mocked example' },
    { label: 'Avg. Days on Market', value: '38', note: 'Mocked example' },
    { label: 'Avg. Rental Rate (2BR)', value: '$3,100/mo', note: 'Mocked example' },
  ],
};

export const specialistSpotlight: SpecialistSpotlight = {
  name: 'Mahsheed Parsons',
  title: 'Panorama Owner · Luxury Home Specialist · Board Member',
  bio: 'Mahsheed Parsons is a Panorama Towers owner, a board member, and a licensed luxury real estate specialist with deep knowledge of the Panorama market. As a resident herself, she brings an insider perspective to both buying and selling within the community.',
  specialties: [
    'Panorama Towers listings and sales',
    'Luxury high-rise transactions',
    'Townhome and tower unit expertise',
    'Second-home and investment property guidance',
    'Resident-to-resident transaction support',
  ],
  note: 'This spotlight is for informational purposes. It does not constitute an endorsement or referral. Contact information is not published here — connect through the resident directory or community channels.',
};

export const ownerResources = [
  {
    id: 'or-1',
    title: 'Understanding Your HOA',
    description: 'Key information about the Panorama HOA structure, dues, and what they cover.',
    icon: '🏛️',
  },
  {
    id: 'or-2',
    title: 'Thinking of Selling?',
    description: 'A practical overview of what to consider before listing your Panorama unit.',
    icon: '📋',
  },
  {
    id: 'or-3',
    title: 'Leasing Your Unit',
    description: 'What Panorama owners need to know about leasing their unit — rules, process, and considerations.',
    icon: '📄',
  },
  {
    id: 'or-4',
    title: 'Townhome vs. Tower: What Buyers Should Know',
    description: 'The key differences between Panorama\'s tower units and townhomes — layout, access, and lifestyle.',
    icon: '🏢',
  },
];

export function getREListingBySlug(slug: string): RealEstateListing | undefined {
  return mockListingsRE.find((l) => l.slug === slug);
}
